import React from 'react';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import classnames from 'classnames';
import { useLocation } from 'react-router-dom';
import { getHours, getMinutes } from 'date-fns';
import { addFirstZero, addLeadingZero } from 'helpers/helpers';
import { useTimeSettingsFormat } from 'common/useTimeSettingsFormat';
import { convertTime } from 'common/dateFormatConfig';
import useWorklogStyles from 'components/WorklogDetails/hooks/useWorklogStyles';
import useDiffHighlight from 'components/WorklogDetails/hooks/useDiffHighlight';
import { useResourceHoursChanges } from 'components/WorklogDetails/hooks/useResourceHoursChanges';

const isMobile = window.innerWidth <= 700;

const getStartTimeParts = (entity) => {
  if (!entity) return null;
  if (entity.startTimeDate) {
    const d = new Date(entity.startTimeDate);
    return { hour: getHours(d), minute: getMinutes(d) };
  }
  if (entity.startTime) {
    return {
      hour: Number(entity.startTime.hour),
      minute: Number(entity.startTime.minute),
    };
  }
  return null;
};

const isStartTimeDifferentFromWl = (worker, sheet) => {
  const w = getStartTimeParts(worker);
  const s = getStartTimeParts(sheet);
  if (!w || !s) return false;
  return w.hour !== s.hour || w.minute !== s.minute;
};

const isHoursRangeDifferentFromWl = (h, sheetHours) => {
  if (!h || !sheetHours) return false;
  const ws = h.start ? new Date(h.start).getTime() : null;
  const we = h.end ? new Date(h.end).getTime() : null;
  const ss = sheetHours.start ? new Date(sheetHours.start).getTime() : null;
  const se = sheetHours.end ? new Date(sheetHours.end).getTime() : null;
  return ws !== ss || we !== se;
};

const isTravelToDifferentFromWl = (h, sheetHours) =>
  (Number(h?.travelTimeTo?.hours) || 0) !== (Number(sheetHours?.travelTimeTo?.hours) || 0)
  || (Number(h?.travelTimeTo?.minutes) || 0) !== (Number(sheetHours?.travelTimeTo?.minutes) || 0);

const isTravelFromDifferentFromWl = (h, sheetHours) =>
  (Number(h?.travelTimeFrom?.hours) || 0) !== (Number(sheetHours?.travelTimeFrom?.hours) || 0)
  || (Number(h?.travelTimeFrom?.minutes) || 0) !== (Number(sheetHours?.travelTimeFrom?.minutes) || 0);

const TimeInfo = ({
  classes,
  workerData,
  sheet,
  getFormattedHoursRange,
  isWorkerScheduleMobile,
  isItemAdded,
  isItemChanged,
  historyHours,
  historyState,
}) => {
  const worklogClasses = useWorklogStyles();
  const diffClass = useDiffHighlight();
  const location = useLocation();

  const getTimeFormatHook = useTimeSettingsFormat();

  const isWorkerSchedulePage = /schedule-user/i.test(location.pathname);

  const workerHours = workerData?.hours?.length ? workerData.hours : [];

  const hasIndividualStartTime = !!(workerData?.startTime || workerData?.startTimeDate);
  const startTimeParts = getStartTimeParts(workerData);
  const startTimeDisplay = startTimeParts
    ? { hour: addFirstZero(startTimeParts.hour), minute: addFirstZero(startTimeParts.minute) }
    : null;
  const workerStartTime = startTimeDisplay
    ? convertTime(startTimeDisplay, getTimeFormatHook().is12Format)
    : null;
  const startTimeDiffersFromWl = isWorkerSchedulePage
    && hasIndividualStartTime
    && isStartTimeDifferentFromWl(workerData, sheet);

  const { hoursChanges, removedHours } = useResourceHoursChanges(workerData?.hours, historyHours, isItemAdded);

  const isTravelTimeOnlyChanged = historyState?.diff?.travelTimeOnly?.length;

  const renderHours = (h, options) => {
    const onJobDiffsFromWl = isWorkerSchedulePage && isHoursRangeDifferentFromWl(h, sheet.hours);
    const travelToDiffsFromWl = isWorkerSchedulePage && isTravelToDifferentFromWl(h, sheet.hours);
    const travelFromDiffsFromWl = isWorkerSchedulePage && isTravelFromDifferentFromWl(h, sheet.hours);

    return (
      <div
        className={classnames(
          classes.gridCellWrapper,
          isItemChanged && (options?.hoursAdded || options?.allChanged) ? worklogClasses.showDiff : null,
          isItemChanged && options?.hoursRemoved ? worklogClasses.showDiffRemoved : null,
        )}
      >
        <Grid item xs={5} className={classnames(classes.gridCell)}>
          <Grid key={h.id}>
            <Typography
              variant="body1"
              color="textPrimary"
              className={classnames(
                classes.hoursPart,
                (isItemChanged && options?.hoursChanged && !h.isWlHours) ? worklogClasses.showDiff : null,
                (isTravelTimeOnlyChanged && sheet.travelTimeOnly) ? worklogClasses.showDiffRemoved : null,
                diffClass({ changed: onJobDiffsFromWl }),
              )}
            >
              {(sheet.travelTimeOnly && !isTravelTimeOnlyChanged) || sheet.isWlHours
                ? 'N/A'
                : getFormattedHoursRange(h)}
            </Typography>
          </Grid>
        </Grid>
        <Grid item xs={3} className={classnames(classes.gridCell)}>
          <Typography
            variant="body1"
            color="textPrimary"
            className={classnames(
              classes.hoursPart,
              (isItemChanged && options?.travelTimeToChanged && !h.isWlTravelTime) ? worklogClasses.showDiff : null,
              diffClass({ changed: travelToDiffsFromWl }),
            )}
          >
            {h?.isWlTravelTime ? (
              'N/A'
            ) : (
              <>
                {addLeadingZero(h?.travelTimeTo?.hours)}h{' '}
                {addLeadingZero(h?.travelTimeTo?.minutes)}m
              </>
            )}
          </Typography>
        </Grid>
        <Grid item xs={4} className={classnames(classes.gridCell)} style={{ paddingLeft: 15 }}>
          <Typography
            variant="body1"
            color="textPrimary"
            className={classnames(
              classes.hoursPart,
              (isItemChanged && options?.travelTimeFromChanged && !h.isWlTravelTime) ? worklogClasses.showDiff : null,
              diffClass({ changed: travelFromDiffsFromWl }),
            )}
          >
            {h?.isWlTravelTime ? (
              'N/A'
            ) : (
              <>
                {addLeadingZero(h?.travelTimeFrom?.hours)}h{' '}
                {addLeadingZero(h?.travelTimeFrom?.minutes)}m
              </>
            )}
          </Typography>
        </Grid>
      </div>
    );
  };

  return (
    <>
      {isMobile ? (
        <div className={classnames(classes.gridsWrapper)}>
          {hasIndividualStartTime && (
            <div className={classnames(classes.rowWrapper)}>
              <div className={classnames(classes.gridCellWrapper)}>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  className={classes.regularFontWeight}
                >
                  Start Time:
                </Typography>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  className={diffClass({ changed: startTimeDiffersFromWl })}
                >
                  {workerStartTime}
                </Typography>
              </div>
            </div>
          )}
          {(workerHours.length || (isItemChanged && removedHours?.length)) ? (
            <>
              <div className={classnames(classes.rowWrapper)}>
                {workerHours.map((h, i) => {
                  const changes = hoursChanges?.[i];
                  const cellAdded = isItemChanged && (changes?.hoursAdded || changes?.allChanged);
                  const cellChanged = isItemChanged && changes?.hoursChanged && !h.isWlHours;
                  return (
                    <div className={classnames(classes.gridCellWrapper)} key={h.id || i}>
                      {i === 0 ? (
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          className={classes.regularFontWeight}
                        >
                          On Job Time:
                        </Typography>
                      ) : null}
                      <Typography
                        variant="body1"
                        color="textPrimary"
                        className={classnames(
                          diffClass({ changed: isWorkerSchedulePage && isHoursRangeDifferentFromWl(h, sheet.hours) }),
                          (cellAdded || cellChanged) ? worklogClasses.showDiff : null,
                          (isTravelTimeOnlyChanged && sheet.travelTimeOnly) ? worklogClasses.showDiffRemoved : null,
                        )}
                      >
                        {(sheet.travelTimeOnly && !isTravelTimeOnlyChanged) || sheet.isWlHours
                          ? 'N/A'
                          : getFormattedHoursRange(h)}
                      </Typography>
                    </div>
                  );
                })}
                {isItemChanged && removedHours?.map((h) => (
                  <div className={classnames(classes.gridCellWrapper)} key={`removed-onjob-${h.id}`}>
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      className={worklogClasses.showDiffRemoved}
                    >
                      {getFormattedHoursRange(h)}
                    </Typography>
                  </div>
                ))}
              </div>
              <div className={classnames(classes.rowWrapper)}>
                {workerHours.map((h, i) => {
                  const changes = hoursChanges?.[i];
                  const cellAdded = isItemChanged && (changes?.hoursAdded || changes?.allChanged);
                  const cellChanged = isItemChanged && changes?.travelTimeToChanged && !h.isWlTravelTime;
                  return (
                    <div className={classnames(classes.gridCellWrapper)} key={h.id || i}>
                      {i === 0 ? (
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          className={classes.regularFontWeight}
                        >
                          Individual Time To:
                        </Typography>
                      ) : null}
                      <Typography
                        variant="body1"
                        color="textPrimary"
                        className={classnames(
                          diffClass({ changed: isWorkerSchedulePage && isTravelToDifferentFromWl(h, sheet.hours) }),
                          (cellAdded || cellChanged) ? worklogClasses.showDiff : null,
                        )}
                      >
                        {h?.isWlTravelTime ? (
                          'N/A'
                        ) : (
                          <>
                            {addLeadingZero(h?.travelTimeTo?.hours)}h{' '}
                            {addLeadingZero(h?.travelTimeTo?.minutes)}m
                          </>
                        )}
                      </Typography>
                    </div>
                  );
                })}
                {isItemChanged && removedHours?.map((h) => (
                  <div className={classnames(classes.gridCellWrapper)} key={`removed-to-${h.id}`}>
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      className={worklogClasses.showDiffRemoved}
                    >
                      {addLeadingZero(h?.travelTimeTo?.hours)}h{' '}
                      {addLeadingZero(h?.travelTimeTo?.minutes)}m
                    </Typography>
                  </div>
                ))}
              </div>
              <div className={classnames(classes.rowWrapper)}>
                {workerHours.map((h, i) => {
                  const changes = hoursChanges?.[i];
                  const cellAdded = isItemChanged && (changes?.hoursAdded || changes?.allChanged);
                  const cellChanged = isItemChanged && changes?.travelTimeFromChanged && !h.isWlTravelTime;
                  return (
                    <div className={classnames(classes.gridCellWrapper)} key={h.id || i}>
                      {i === 0 ? (
                        <Typography
                          variant="body1"
                          color="textSecondary"
                          className={classes.regularFontWeight}
                        >
                          Individual Time From:
                        </Typography>
                      ) : null}
                      <Typography
                        variant="body1"
                        color="textPrimary"
                        className={classnames(
                          diffClass({ changed: isWorkerSchedulePage && isTravelFromDifferentFromWl(h, sheet.hours) }),
                          (cellAdded || cellChanged) ? worklogClasses.showDiff : null,
                        )}
                      >
                        {h?.isWlTravelTime ? (
                          'N/A'
                        ) : (
                          <>
                            {addLeadingZero(h?.travelTimeFrom?.hours)}h{' '}
                            {addLeadingZero(h?.travelTimeFrom?.minutes)}m
                          </>
                        )}
                      </Typography>
                    </div>
                  );
                })}
                {isItemChanged && removedHours?.map((h) => (
                  <div className={classnames(classes.gridCellWrapper)} key={`removed-from-${h.id}`}>
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      className={worklogClasses.showDiffRemoved}
                    >
                      {addLeadingZero(h?.travelTimeFrom?.hours)}h{' '}
                      {addLeadingZero(h?.travelTimeFrom?.minutes)}m
                    </Typography>
                  </div>
                ))}
              </div>
            </>
          ) : null}
        </div>
      ) : (
        <>
          {workerHours?.map((h, i) => (
            renderHours(h, hoursChanges?.[i])
          ))}
          {isItemChanged ? removedHours?.map((h) => (
            renderHours(h, { hoursRemoved: true })
          )) : null}
        </>
      )}
    </>
  );
};

export default TimeInfo;
